import React, { FC, useEffect } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { FormattedMessage } from 'react-intl';
import { Box, makeStyles, Typography } from '@material-ui/core';
import * as actions from 'common/components/PersonProfile/state/actions';
import * as selectors from 'common/components/PersonProfile/state/selectors';
import { useAppDispatch } from 'store/hooks';
import { PeakModules } from 'common/constants/peakModules';
import { IInvoiceDetailsImt } from 'common/interfaces/invoices';
import { DialogComponent, LoadingBackdrop } from 'common/components/index';
import {
  CheckOutResponseNamesInfo,
  CheckOutResponsePaymentInfo,
} from 'common/components/InvoiceOperating/InvoiceModals';
import messages from 'common/messages/messages';
import InvoiceStatus from 'components/Invoice/InvoiceStatus';

const useStyles = makeStyles(() => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  content: {
    minHeight: '300px',
  },
}));

interface IInvoiceDetailsModal {
  onClose: () => void;
  personId: number;
  invoiceId: string;
  module: PeakModules;
}

const InvoiceDetailsModal: FC<IInvoiceDetailsModal> = ({
  onClose,
  personId,
  invoiceId,
  module,
}) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  let title: JSX.Element | undefined;

  const invoiceDetails: IInvoiceDetailsImt = useRootSelector(
    selectors.selectPaymentDetails(personId),
  );

  const invoiceDetailsLoading: boolean = useRootSelector(
    selectors.selectPaymentDetailsLoading(personId),
  );

  useEffect(() => {
    dispatch(actions.fetchPaymentDetails(personId, invoiceId, module));

    return () => {
      dispatch(actions.resetPaymentDetailsAction(personId));
    };
  }, [dispatch, invoiceId, module, personId]);

  if (invoiceDetails) {
    title = (
      <div className={classes.title}>
        <Typography variant="h4">
          <FormattedMessage
            {...messages.invoiceNumber}
            values={{ number: invoiceDetails?.get('number') }}
          />
        </Typography>

        <InvoiceStatus status={invoiceDetails.get('status')} />
      </div>
    );
  }

  return (
    <DialogComponent isOpen onClose={onClose} title={title} hideFooterButtons>
      <Box className={classes.content}>
        {invoiceDetails?.size ? (
          <>
            <CheckOutResponseNamesInfo checkOutData={invoiceDetails} />
            <CheckOutResponsePaymentInfo checkOutData={invoiceDetails} />
          </>
        ) : null}

        <LoadingBackdrop isLoading={invoiceDetailsLoading} />
      </Box>
    </DialogComponent>
  );
};

export default InvoiceDetailsModal;
